import { getStoredPermissions, can } from './permissions'
import { getLandingForRoles } from '@/config/roleSidebarMap'

const PUBLIC_ROUTE_NAMES = ['Login', 'Register', 'Page404', 'Page500', '404']

const ROLE_GROUPS = {
  // 'finance_manager', 'senior_accountant_2' and 'hr_manager' are treated as
  // admin-equivalent everywhere so they inherit every requiresAdmin /
  // allowedRoleGroups:['admin', …] route. Their carve-outs are gated separately:
  //   - finance_manager:     Settings          → requiresPermission: 'manage_settings'
  //   - senior_accountant_2: Settings + Attendance → 'manage_settings' + 'view_attendance'
  //   - hr_manager:          Settings (via 'manage_settings') PLUS a specific page
  //                          set blocked via meta.blockedRoles (analytics, inbox,
  //                          shipping dashboard, POS, field sales form, scanner,
  //                          packing batches, mark returns). Its only settings-style
  //                          surface is the scoped /staff Users page.
  admin: ['admin', 'superadmin', 'finance_manager', 'senior_accountant_2', 'hr_manager'],
  user: ['user', 'admin', 'superadmin', 'finance_manager', 'senior_accountant_2', 'hr_manager'],
  hr_manager: ['hr_manager', 'admin', 'superadmin', 'finance_manager', 'senior_accountant_2'],
}

export function normalizeRoles(rawRoles) {
  if (Array.isArray(rawRoles)) {
    return rawRoles.map((role) => String(role).trim()).filter(Boolean)
  }

  if (typeof rawRoles === 'string') {
    return rawRoles
      .split(',')
      .map((role) => role.trim())
      .filter(Boolean)
  }

  return []
}

export function getStoredToken() {
  return localStorage.getItem('api_token')
}

export function getStoredRoles() {
  return normalizeRoles(localStorage.getItem('roles'))
}

export function isAuthenticated() {
  return Boolean(getStoredToken())
}

export function isPublicRoute(route) {
  if (PUBLIC_ROUTE_NAMES.includes(route.name)) {
    return true
  }

  return route.matched.some((record) => record.meta && record.meta.public === true)
}

export function resolveRequiredRoleGroups(route) {
  const requiredGroups = []

  route.matched.forEach((record) => {
    const meta = record.meta || {}

    if (Array.isArray(meta.allowedRoleGroups)) {
      requiredGroups.push(...meta.allowedRoleGroups)
      return
    }

    if (meta.requiresAdmin) {
      requiredGroups.push('admin')
      return
    }

    if (meta.requiresUser) {
      requiredGroups.push('user')
    }
  })

  return [...new Set(requiredGroups)]
}

// Deny-list: a route (or any of its matched ancestors) may name roles that must
// NOT reach it, even if they'd otherwise pass the allow-list. Used to carve
// specific pages out of an admin-equivalent role (e.g. hr_manager) without
// touching the many other roles that legitimately reach those pages.
export function resolveBlockedRoles(route) {
  const blocked = []

  route.matched.forEach((record) => {
    const meta = record.meta || {}
    if (Array.isArray(meta.blockedRoles)) {
      blocked.push(...meta.blockedRoles)
    }
  })

  return [...new Set(blocked)]
}

export function isBlockedByRole(blockedRoles, roles = getStoredRoles()) {
  if (!blockedRoles.length) {
    return false
  }
  return roles.some((role) => blockedRoles.includes(role))
}

export function resolveRequiredPermissions(route) {
  const required = []

  route.matched.forEach((record) => {
    const meta = record.meta || {}

    if (Array.isArray(meta.requiresPermission)) {
      required.push(...meta.requiresPermission)
    } else if (typeof meta.requiresPermission === 'string') {
      required.push(meta.requiresPermission)
    }
  })

  return [...new Set(required)]
}

export function hasRequiredRoleGroups(requiredGroups, roles = getStoredRoles()) {
  if (requiredGroups.length === 0) {
    return true
  }

  return requiredGroups.some((group) => {
    const mappedRoles = ROLE_GROUPS[group] || [group]
    return mappedRoles.some((role) => roles.includes(role))
  })
}

export function hasRequiredPermissions(requiredPermissions, permissions = getStoredPermissions()) {
  if (requiredPermissions.length === 0) {
    return true
  }

  return requiredPermissions.every((permission) => can(permission, permissions))
}

export function getRouteAccessDecision(route) {
  const token = getStoredToken()
  const publicRoute = isPublicRoute(route)
  const requiredGroups = resolveRequiredRoleGroups(route)
  const requiredPermissions = resolveRequiredPermissions(route)
  const blockedRoles = resolveBlockedRoles(route)

  if (!publicRoute && !token) {
    return {
      allowed: false,
      redirectPath: '/login',
      includeNextUrl: true,
    }
  }

  if ((route.name === 'Login' || route.name === 'Register') && token) {
    return {
      allowed: false,
      redirectPath: getLandingForRoles(localStorage.getItem('roles')),
      includeNextUrl: false,
    }
  }

  if (!hasRequiredRoleGroups(requiredGroups) || !hasRequiredPermissions(requiredPermissions) || isBlockedByRole(blockedRoles)) {
    // Authenticated but blocked → silently route the user to their role's
    // landing page rather than showing a 403. Avoid a redirect loop if the
    // landing itself is the page being blocked (defensive fallback).
    if (token) {
      const landing = getLandingForRoles(localStorage.getItem('roles'))
      const attempted = route && route.path
      const safeLanding = landing && landing !== attempted ? landing : '/pages/403'
      return {
        allowed: false,
        redirectPath: safeLanding,
        includeNextUrl: false,
      }
    }
    return {
      allowed: false,
      redirectPath: '/login',
      includeNextUrl: true,
    }
  }

  return {
    allowed: true,
    redirectPath: null,
    includeNextUrl: false,
  }
}
